// Shared utilities public API
import { getApiUrl } from '@/config/api.config';

/**
 * Constructs a full URL for an R2 asset path
 * Converts relative paths like "/assets/rooms/room-123.png" to full backend URLs
 *
 * @param assetPath - The asset path (e.g., "/assets/rooms/room-123.png")
 * @returns Full URL (e.g., "http://localhost:3001/assets/rooms/room-123.png") or null if no path
 *
 * @example
 * getAssetUrl('/assets/rooms/room-123.png')
 * // Returns: 'http://localhost:3001/assets/rooms/room-123.png'
 *
 * getAssetUrl('https://lh3.googleusercontent.com/...')
 * // Returns: 'https://lh3.googleusercontent.com/...' (unchanged for full URLs)
 *
 * getAssetUrl(null)
 * // Returns: null
 */
export function getAssetUrl(assetPath: string | null | undefined): string | null {
  if (!assetPath) return null;

  // If already a full URL (e.g., Google CDN for avatars), return as-is
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath;
  }

  // Construct full URL with backend base URL (without /api/v1)
  // Use centralized API config instead of hardcoded fallback
  const apiUrl = getApiUrl();

  // Remove /api/v1 suffix to get base backend URL
  // Example: "http://localhost:4200/api/v1" -> "http://localhost:4200"
  const baseUrl = apiUrl.replace(/\/api\/v1$/, '');

  // Remove leading slash if present (will be added by URL construction)
  const path = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;

  return `${baseUrl}/${path}`;
}
